import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, WrenchIcon, ZapIcon, ThermometerIcon, HomeIcon, PaintbrushIcon, SparklesIcon, TreesIcon, HammerIcon, ClockIcon, UserIcon, MapPinIcon, ShowerHeadIcon, BathIcon, ArrowUpIcon, SquareIcon, SunIcon, CookingPotIcon, LayersIcon, DoorOpenIcon, WarehouseIcon, ShieldIcon, FenceIcon, SnowflakeIcon, FootprintsIcon, BugIcon, SearchIcon, RefreshCwIcon, HelpCircleIcon, LoaderIcon, CloudRainIcon, TreePineIcon } from 'lucide-react';
import { pxFormConfigs } from '../data/pxFormConfigs';
import { PXQuoteForm } from './PXQuoteForm';
interface QuoteFormProps {
  initialService: string;
  initialZip: string;
  onClose: () => void;
}
export function QuoteForm({
  initialService,
  initialZip,
  onClose
}: QuoteFormProps) {
  // Route window-replacement to specialized flow
  if (initialService === 'window-replacement') {
    return <WindowReplacementForm initialZip={initialZip} onClose={onClose} data-id="element-2339" />;
  }
  if (initialService === 'roofing') {
    return <RoofingQuoteForm initialZip={initialZip} onClose={onClose} data-id="element-2340" />;
  }
  // Route to data-driven PX form if config exists
  if (pxFormConfigs[initialService]) {
    return <PXQuoteForm config={pxFormConfigs[initialService]} initialZip={initialZip} onClose={onClose} data-id="element-2341" />;
  }
  return <GenericQuoteForm initialService={initialService} initialZip={initialZip} onClose={onClose} data-id="element-2342" />;
}
// ─── WINDOW REPLACEMENT FORM (mirrors Champion flow) ───────────────────────
function WindowReplacementForm({
  initialZip,
  onClose
}: {
  initialZip: string;
  onClose: () => void;
}) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [zipLookupLoading, setZipLookupLoading] = useState(false);
  const [zipLookupDone, setZipLookupDone] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [editingZip, setEditingZip] = useState(false);
  const [formData, setFormData] = useState({
    formName: 'windows_replacement_form_v1',
    alid: '',
    oid: '',
    source: 'quote_form',
    subID1: '',
    subID2: '',
    subID3: '',
    EBRDate: '',
    ip: '',
    cert: '',
    projectNature: '',
    windowCount: '',
    homeOwnership: '',
    taskName: 'Windows',
    address1: '',
    city: '',
    state: '',
    postal_code: initialZip || '',
    first_name: '',
    last_name: '',
    email: '',
    phone_primary: ''
  });
  // Zip code lookup
  useEffect(() => {
    const zip = formData.postal_code;
    if (zip && zip.length === 5) {
      setZipLookupLoading(true);
      setZipLookupDone(false);
      fetch(`https://api.zippopotam.us/us/${zip}`).then(res => {
        if (!res.ok) throw new Error('Invalid zip');
        return res.json();
      }).then(data => {
        if (data.places && data.places.length > 0) {
          const place = data.places[0];
          setFormData(prev => ({
            ...prev,
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          }));
          setZipLookupDone(true);
        }
      }).catch(() => {
        setZipLookupDone(false);
      }).finally(() => {
        setZipLookupLoading(false);
      });
    }
  }, [formData.postal_code]);
  // Phone formatting
  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      phone_primary: formatPhoneNumber(e.target.value)
    }));
  };
  // TrustedForm
  useEffect(() => {
    try {
      const tf = document.createElement('script');
      tf.type = 'text/javascript';
      tf.async = true;
      tf.src = 'https://api.trustedform.com/trustedform.js?provide_referrer=false&field=xxTrustedFormCertUrl&l=' + new Date().getTime() + Math.random();
      document.head.appendChild(tf);
    } catch (e) {
      console.warn('TrustedForm script could not be loaded:', e);
    }
    const interval = setInterval(() => {
      try {
        const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
        if (certInput && certInput.value) {
          setFormData(prev => ({
            ...prev,
            cert: certInput.value
          }));
          clearInterval(interval);
        }
      } catch (e) {}
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  // Fetch IP
  useEffect(() => {
    try {
      fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => {
        if (data?.ip) setFormData(prev => ({
          ...prev,
          ip: data.ip
        }));
      }).catch(() => {});
    } catch (e) {}
  }, []);
  const totalSteps = 5;
  const progress = step / totalSteps * 100;
  const handleNext = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const handleOptionSelect = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setTimeout(() => handleNext(), 300);
  };
  const validateAddress = (address: string): boolean => {
    return /^\d+\s+\S{2,}/.test(address.trim());
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'address1') setAddressError('');
  };
  const handleAddressBlur = () => {
    if (formData.address1 && !validateAddress(formData.address1)) {
      setAddressError('Please enter a valid street address (e.g. 123 Main St)');
    } else {
      setAddressError('');
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
    const finalCert = certInput?.value || formData.cert;
    const {
      cert,
      ...restFormData
    } = formData;
    const payload = {
      ...restFormData,
      TrustedFormCert: finalCert,
      api_token: '88E0DE0F-E8D4-4C49-8131-7FAB6AD9DC4E'
    };
    const params = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (value) params.append(key, String(value));
    });
    const webhookUrl = `https://hooks.zapier.com/hooks/catch/6973915/uxzsuc2/?${params.toString()}`;
    console.log('Submitting to Window Replacement webhook:', webhookUrl);
    try {
      await fetch(webhookUrl, {
        method: 'GET',
        mode: 'no-cors'
      });
    } catch (err) {
      console.warn('Webhook call failed, but continuing:', err);
    }
    setIsSubmitting(false);
    setIsSuccess(true);
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const Header = () => <header className="bg-white border-b border-slate-200 py-4 px-4 md:px-8 flex justify-between items-center sticky top-0 z-10" data-id="element-2343">
      <div className="text-xl font-extrabold text-slate-900 tracking-tight" data-id="element-2344">
        FindService<span className="text-amber-500" data-id="element-2345">Quotes</span>
      </div>
      <button onClick={onClose} className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1" data-id="element-2346">
        <ArrowLeftIcon className="w-4 h-4" data-id="element-2347" />
        <span className="hidden sm:inline" data-id="element-2348">Exit</span>
      </button>
    </header>;
  if (isSuccess) {
    return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-2349">
        <Header data-id="element-2350" />
        <div className="flex-1 flex items-center justify-center p-6" data-id="element-2351">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center border border-slate-100" data-id="element-2352">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2353">
              <CheckCircle2Icon className="w-10 h-10 text-green-600" data-id="element-2354" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2355">
              Quote Request Received!
            </h2>
            <p className="text-slate-600 mb-8" data-id="element-2356">
              Thank you, {formData.first_name}! A window replacement specialist
              will contact you shortly at {formData.phone_primary} to discuss
              your {formData.projectNature.toLowerCase()} project in{' '}
              {formData.city}.
            </p>
            <button onClick={onClose} className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-colors" data-id="element-2357">
              Return to Home
            </button>
          </motion.div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-2358">
      <Header data-id="element-2359" />
      <input type="hidden" name="xxTrustedFormCertUrl" data-id="element-2360" />

      <div className="w-full bg-slate-200 h-2" data-id="element-2361">
        <motion.div className="bg-amber-500 h-full" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-2362" />
      </div>

      <main className="flex-1 flex flex-col items-center pt-8 pb-20 px-4" data-id="element-2363">
        <div className="w-full max-w-2xl" data-id="element-2364">
          <div className="mb-6 h-8" data-id="element-2365">
            {step > 1 && <button onClick={handleBack} className="text-slate-500 hover:text-slate-900 font-medium flex items-center gap-2 transition-colors" data-id="element-2366">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2367" /> Back
              </button>}
          </div>

          <div className="relative bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-slate-100" data-id="element-2368">
            <AnimatePresence mode="wait" custom={direction} data-id="element-2369">
              {/* STEP 1: Nature of Project */}
              {step === 1 && <motion.div key="step1" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2370">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2371">
                    What is the nature of your windows project?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2372">
                    {[{
                  value: 'Replace',
                  desc: 'I need to replace existing windows',
                  icon: <RefreshCwIcon className="w-6 h-6" data-id="element-2373" />
                }, {
                  value: 'Repair',
                  desc: 'I need to repair existing windows',
                  icon: <WrenchIcon className="w-6 h-6" data-id="element-2374" />
                }, {
                  value: 'Not Sure',
                  desc: "I'm not sure yet — I'd like an expert opinion",
                  icon: <HelpCircleIcon className="w-6 h-6" data-id="element-2375" />
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('projectNature', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.projectNature === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2376">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.projectNature === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2377">
                          {option.icon}
                        </div>
                        <div data-id="element-2378">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2379">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2380">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 2: How Many Windows */}
              {step === 2 && <motion.div key="step2" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2381">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2382">
                    How many windows are involved?
                  </h1>
                  <div className="grid grid-cols-2 gap-4" data-id="element-2383">
                    {['6+', '3-5', '2', '1'].map((count, idx) => <button key={idx} onClick={() => handleOptionSelect('windowCount', count)} className={`p-8 rounded-xl border-2 flex flex-col items-center justify-center gap-3 transition-all duration-200 ${formData.windowCount === count ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2384">
                        <span className="font-bold text-slate-900 text-2xl" data-id="element-2385">
                          {count}
                        </span>
                        <span className="text-slate-500 text-sm" data-id="element-2386">
                          {count === '1' ? 'window' : 'windows'}
                        </span>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 3: Homeownership */}
              {step === 3 && <motion.div key="step3" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2387">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2388">
                    Do you own your home?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2389">
                    {[{
                  value: 'Yes',
                  desc: 'I own this property'
                }, {
                  value: 'No - But I am Allowed to make changes',
                  desc: 'I rent but have permission from my landlord'
                }, {
                  value: 'No',
                  desc: 'I rent and do not have permission'
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('homeOwnership', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.homeOwnership === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2390">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.homeOwnership === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2391">
                          <UserIcon className="w-6 h-6" data-id="element-2392" />
                        </div>
                        <div data-id="element-2393">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2394">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2395">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 4: Address */}
              {step === 4 && <motion.div key="step4" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2396">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2397">
                    Where is your project located?
                  </h1>
                  <div className="space-y-5" data-id="element-2398">
                    <div data-id="element-2399">
                      <label htmlFor="address1" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2400">
                        Street Address
                      </label>
                      <input type="text" id="address1" name="address1" value={formData.address1} onChange={handleInputChange} onBlur={handleAddressBlur} className={`w-full h-14 px-4 bg-slate-50 border rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent ${addressError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`} placeholder="123 Main St" data-id="element-2401" />
                      {addressError && <p className="text-red-500 text-xs mt-1.5 font-medium" data-id="element-2402">
                          {addressError}
                        </p>}

                      <div className="mt-3 flex items-center gap-2" data-id="element-2403">
                        {zipLookupLoading ? <div className="flex items-center gap-2 text-slate-400 text-sm" data-id="element-2404">
                            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2405" />
                            <span data-id="element-2406">Looking up location...</span>
                          </div> : editingZip ? <div className="flex items-center gap-2 w-full" data-id="element-2407">
                            <MapPinIcon className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-2408" />
                            <input type="text" value={formData.postal_code} onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setFormData(prev => ({
                          ...prev,
                          postal_code: val,
                          city: '',
                          state: ''
                        }));
                        setZipLookupDone(false);
                      }} onBlur={() => {
                        if (formData.postal_code.length === 5) setEditingZip(false);
                      }} onKeyDown={e => {
                        if (e.key === 'Enter' && formData.postal_code.length === 5) setEditingZip(false);
                      }} placeholder="Enter ZIP" maxLength={5} autoFocus className="flex-1 h-10 px-3 bg-white border border-amber-300 rounded-lg text-sm font-semibold text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" data-id="element-2409" />
                          </div> : zipLookupDone && formData.city && formData.state ? <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-full hover:bg-amber-100 transition-colors cursor-pointer text-left" title="Click to change ZIP code" data-id="element-2410">
                            <MapPinIcon className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-2411" />
                            <span className="text-sm font-semibold text-amber-700" data-id="element-2412">
                              {formData.city}, {formData.state}{' '}
                              {formData.postal_code}
                            </span>
                            <span className="text-[10px] text-amber-400 ml-auto" data-id="element-2413">
                              edit
                            </span>
                          </button> : <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 w-full hover:bg-slate-100 transition-colors cursor-pointer text-left" title="Click to enter ZIP code" data-id="element-2414">
                            <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-2415" />
                            <span className="text-sm text-slate-500" data-id="element-2416">
                              {formData.postal_code ? `Zip: ${formData.postal_code}` : 'Enter your ZIP code'}
                            </span>
                          </button>}
                      </div>
                    </div>

                    <button onClick={() => {
                  if (!validateAddress(formData.address1)) {
                    setAddressError('Please enter a valid street address (e.g. 123 Main St)');
                    return;
                  }
                  handleNext();
                }} disabled={!formData.address1 || !formData.city || !formData.state || !formData.postal_code || !!addressError} className="w-full mt-6 h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold text-lg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2417">
                      Continue
                    </button>
                  </div>
                </motion.div>}

              {/* STEP 5: Contact Info */}
              {step === 5 && <motion.div key="step5" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2418">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2 text-center" data-id="element-2419">
                    Who should we send the quote to?
                  </h1>
                  <p className="text-slate-500 text-center mb-8" data-id="element-2420">
                    Last step! Your information is secure.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2421">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-2422">
                      <div data-id="element-2423">
                        <label htmlFor="first_name" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2424">
                          First Name
                        </label>
                        <input type="text" id="first_name" name="first_name" required value={formData.first_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="First Name" data-id="element-2425" />
                      </div>
                      <div data-id="element-2426">
                        <label htmlFor="last_name" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2427">
                          Last Name
                        </label>
                        <input type="text" id="last_name" name="last_name" required value={formData.last_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="Last Name" data-id="element-2428" />
                      </div>
                    </div>

                    <div data-id="element-2429">
                      <label htmlFor="email" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2430">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="email@example.com" data-id="element-2431" />
                    </div>

                    <div data-id="element-2432">
                      <label htmlFor="phone_primary" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2433">
                        Primary Phone
                      </label>
                      <input type="tel" id="phone_primary" name="phone_primary" required value={formData.phone_primary} onChange={handlePhoneChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="555-123-4567" maxLength={12} data-id="element-2434" />
                    </div>

                    <div className="pt-4" data-id="element-2435">
                      <label className="flex items-start gap-3 mb-4 cursor-pointer" data-id="element-2436">
                        <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="mt-0.5 w-5 h-5 rounded border-slate-300 text-amber-500 focus:ring-amber-500 cursor-pointer flex-shrink-0" required data-id="element-2437" />
                        <span className="text-[11px] text-slate-500 leading-tight" data-id="element-2438">
                          By clicking the button, I provide my electronic
                          signature and express written consent to share my
                          contact information with FindServiceQuotes, including
                          their contractors and affiliates, and up to four home
                          service companies. I authorize them to email, call, or
                          text me for marketing purposes using autodialer
                          technology, AI generated or AI assisted voice, or
                          prerecorded voice messages on the phone number I
                          provided, even if listed on a DoNotCall list. Consent
                          is not a condition to purchase. I agree to the{' '}
                          <a href="#" className="underline text-amber-600" data-id="element-2439">
                            Terms of Use
                          </a>{' '}
                          and{' '}
                          <a href="#" className="underline text-amber-600" data-id="element-2440">
                            Privacy Policy
                          </a>
                          .
                        </span>
                      </label>
                      <button type="submit" disabled={isSubmitting || !consentChecked} className="w-full h-16 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xl rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-amber-500/30" data-id="element-2441">
                        {isSubmitting ? 'Processing...' : 'Get Your Free Quote'}
                      </button>
                    </div>
                  </form>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}
// ─── ROOFING QUOTE FORM (PX API Flow) ──────────────────────────────────────
function RoofingQuoteForm({
  initialZip,
  onClose
}: {
  initialZip: string;
  onClose: () => void;
}) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [zipLookupLoading, setZipLookupLoading] = useState(false);
  const [zipLookupDone, setZipLookupDone] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [editingZip, setEditingZip] = useState(false);
  const [formData, setFormData] = useState({
    formName: 'roofing_quote_form_v1',
    alid: '',
    oid: '',
    source: 'quote_form',
    subID1: '',
    subID2: '',
    subID3: '',
    EBRDate: '',
    ip: '',
    cert: '',
    project_type: '',
    roofing_material: '',
    stories: '',
    timeline: '',
    insurance_claim: '',
    property_ownership: '',
    taskName: 'Roofing',
    address1: '',
    city: '',
    state: '',
    postal_code: initialZip || '',
    first_name: '',
    last_name: '',
    email: '',
    phone_primary: ''
  });
  // Zip code lookup
  useEffect(() => {
    const zip = formData.postal_code;
    if (zip && zip.length === 5) {
      setZipLookupLoading(true);
      setZipLookupDone(false);
      fetch(`https://api.zippopotam.us/us/${zip}`).then(res => {
        if (!res.ok) throw new Error('Invalid zip');
        return res.json();
      }).then(data => {
        if (data.places && data.places.length > 0) {
          const place = data.places[0];
          setFormData(prev => ({
            ...prev,
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          }));
          setZipLookupDone(true);
        }
      }).catch(() => {
        setZipLookupDone(false);
      }).finally(() => {
        setZipLookupLoading(false);
      });
    }
  }, [formData.postal_code]);
  // Phone formatting
  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      phone_primary: formatPhoneNumber(e.target.value)
    }));
  };
  // TrustedForm
  useEffect(() => {
    try {
      const tf = document.createElement('script');
      tf.type = 'text/javascript';
      tf.async = true;
      tf.src = 'https://api.trustedform.com/trustedform.js?provide_referrer=false&field=xxTrustedFormCertUrl&l=' + new Date().getTime() + Math.random();
      document.head.appendChild(tf);
    } catch (e) {
      console.warn('TrustedForm script could not be loaded:', e);
    }
    const interval = setInterval(() => {
      try {
        const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
        if (certInput && certInput.value) {
          setFormData(prev => ({
            ...prev,
            cert: certInput.value
          }));
          clearInterval(interval);
        }
      } catch (e) {}
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  // Fetch IP
  useEffect(() => {
    try {
      fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => {
        if (data?.ip) setFormData(prev => ({
          ...prev,
          ip: data.ip
        }));
      }).catch(() => {});
    } catch (e) {}
  }, []);
  const totalSteps = 8;
  const progress = step / totalSteps * 100;
  const handleNext = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const handleOptionSelect = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setTimeout(() => handleNext(), 300);
  };
  const validateAddress = (address: string): boolean => {
    return /^\d+\s+\S{2,}/.test(address.trim());
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'address1') setAddressError('');
  };
  const handleAddressBlur = () => {
    if (formData.address1 && !validateAddress(formData.address1)) {
      setAddressError('Please enter a valid street address (e.g. 123 Main St)');
    } else {
      setAddressError('');
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
    const finalCert = certInput?.value || formData.cert;
    const {
      cert,
      ...restFormData
    } = formData;
    const payload = {
      ...restFormData,
      TrustedFormCert: finalCert,
      api_token: '88E0DE0F-E8D4-4C49-8131-7FAB6AD9DC4E'
    };
    const params = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (value) params.append(key, String(value));
    });
    const webhookUrl = `https://hooks.zapier.com/hooks/catch/6973915/uxzsuc2/?${params.toString()}`;
    console.log('Submitting to Roofing webhook:', webhookUrl);
    try {
      await fetch(webhookUrl, {
        method: 'GET',
        mode: 'no-cors'
      });
    } catch (err) {
      console.warn('Webhook call failed, but continuing:', err);
    }
    setIsSubmitting(false);
    setIsSuccess(true);
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const Header = () => <header className="bg-white border-b border-slate-200 py-4 px-4 md:px-8 flex justify-between items-center sticky top-0 z-10" data-id="element-2442">
      <div className="text-xl font-extrabold text-slate-900 tracking-tight" data-id="element-2443">
        FindService<span className="text-amber-500" data-id="element-2444">Quotes</span>
      </div>
      <button onClick={onClose} className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1" data-id="element-2445">
        <ArrowLeftIcon className="w-4 h-4" data-id="element-2446" />
        <span className="hidden sm:inline" data-id="element-2447">Exit</span>
      </button>
    </header>;
  if (isSuccess) {
    return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-2448">
        <Header data-id="element-2449" />
        <div className="flex-1 flex items-center justify-center p-6" data-id="element-2450">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center border border-slate-100" data-id="element-2451">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2452">
              <CheckCircle2Icon className="w-10 h-10 text-green-600" data-id="element-2453" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2454">
              Quote Request Received!
            </h2>
            <p className="text-slate-600 mb-8" data-id="element-2455">
              Thank you, {formData.first_name}! A roofing specialist will
              contact you shortly at {formData.phone_primary} to discuss your{' '}
              {formData.project_type.toLowerCase()} project in {formData.city}.
            </p>
            <button onClick={onClose} className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-colors" data-id="element-2456">
              Return to Home
            </button>
          </motion.div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-2457">
      <Header data-id="element-2458" />
      <input type="hidden" name="xxTrustedFormCertUrl" data-id="element-2459" />

      <div className="w-full bg-slate-200 h-2" data-id="element-2460">
        <motion.div className="bg-amber-500 h-full" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-2461" />
      </div>

      <main className="flex-1 flex flex-col items-center pt-8 pb-20 px-4" data-id="element-2462">
        <div className="w-full max-w-2xl" data-id="element-2463">
          <div className="mb-6 h-8" data-id="element-2464">
            {step > 1 && <button onClick={handleBack} className="text-slate-500 hover:text-slate-900 font-medium flex items-center gap-2 transition-colors" data-id="element-2465">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2466" /> Back
              </button>}
          </div>

          <div className="relative bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-slate-100" data-id="element-2467">
            <AnimatePresence mode="wait" custom={direction} data-id="element-2468">
              {/* STEP 1: Project Type */}
              {step === 1 && <motion.div key="step1" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2469">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2470">
                    What type of roofing project do you need?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2471">
                    {[{
                  value: 'Roof Replacement',
                  desc: 'Replace my entire roof or a large section',
                  icon: <RefreshCwIcon className="w-6 h-6" data-id="element-2472" />
                }, {
                  value: 'Roof Repair',
                  desc: 'Fix leaks, damaged shingles, or other issues',
                  icon: <WrenchIcon className="w-6 h-6" data-id="element-2473" />
                }, {
                  value: 'New Roof',
                  desc: 'Install a roof on new construction',
                  icon: <HomeIcon className="w-6 h-6" data-id="element-2474" />
                }, {
                  value: 'Inspection',
                  desc: 'Get a professional roof inspection',
                  icon: <SearchIcon className="w-6 h-6" data-id="element-2475" />
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('project_type', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.project_type === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2476">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.project_type === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2477">
                          {option.icon}
                        </div>
                        <div data-id="element-2478">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2479">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2480">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 2: Roofing Material */}
              {step === 2 && <motion.div key="step2" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2481">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2482">
                    What roofing material do you prefer?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2483">
                    {[{
                  value: 'Asphalt Shingles',
                  desc: 'Most popular and affordable option'
                }, {
                  value: 'Metal',
                  desc: 'Durable and energy-efficient'
                }, {
                  value: 'Tile',
                  desc: 'Classic look with long lifespan'
                }, {
                  value: 'Slate',
                  desc: 'Premium natural stone roofing'
                }, {
                  value: 'Flat / Low-Slope',
                  desc: 'For flat or nearly flat roofs'
                }, {
                  value: 'Not Sure',
                  desc: "I'd like a professional recommendation"
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('roofing_material', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.roofing_material === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2484">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.roofing_material === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2485">
                          <LayersIcon className="w-6 h-6" data-id="element-2486" />
                        </div>
                        <div data-id="element-2487">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2488">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2489">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 3: Number of Stories */}
              {step === 3 && <motion.div key="step3" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2490">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2491">
                    How many stories is your home?
                  </h1>
                  <div className="grid grid-cols-3 gap-4" data-id="element-2492">
                    {['1 Story', '2 Stories', '3+ Stories'].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('stories', option)} className={`p-8 rounded-xl border-2 flex flex-col items-center justify-center gap-3 transition-all duration-200 ${formData.stories === option ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2493">
                          <HomeIcon className={`w-8 h-8 ${formData.stories === option ? 'text-amber-600' : 'text-slate-400'}`} data-id="element-2494" />
                          <span className="font-bold text-slate-900 text-lg" data-id="element-2495">
                            {option}
                          </span>
                        </button>)}
                  </div>
                </motion.div>}

              {/* STEP 4: Timeline */}
              {step === 4 && <motion.div key="step4" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2496">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2497">
                    When do you need this done?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2498">
                    {[{
                  value: 'Immediately',
                  desc: 'Emergency or urgent repair needed',
                  icon: <ZapIcon className="w-6 h-6" data-id="element-2499" />
                }, {
                  value: 'Within 1 Month',
                  desc: 'Ready to start soon',
                  icon: <ClockIcon className="w-6 h-6" data-id="element-2500" />
                }, {
                  value: '1-3 Months',
                  desc: 'Planning ahead',
                  icon: <ClockIcon className="w-6 h-6" data-id="element-2501" />
                }, {
                  value: '3-6 Months',
                  desc: 'Flexible timeline',
                  icon: <ClockIcon className="w-6 h-6" data-id="element-2502" />
                }, {
                  value: 'Just Researching',
                  desc: 'Gathering information and quotes',
                  icon: <SearchIcon className="w-6 h-6" data-id="element-2503" />
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('timeline', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.timeline === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2504">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.timeline === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2505">
                          {option.icon}
                        </div>
                        <div data-id="element-2506">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2507">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2508">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 5: Insurance Claim */}
              {step === 5 && <motion.div key="step5" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2509">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2510">
                    Is this an insurance claim?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2511">
                    {[{
                  value: 'Yes',
                  desc: 'I have or plan to file an insurance claim',
                  icon: <ShieldIcon className="w-6 h-6" data-id="element-2512" />
                }, {
                  value: 'No',
                  desc: 'This is not an insurance-related project',
                  icon: <HomeIcon className="w-6 h-6" data-id="element-2513" />
                }, {
                  value: 'Not Sure',
                  desc: "I'm not sure if my damage is covered",
                  icon: <HelpCircleIcon className="w-6 h-6" data-id="element-2514" />
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('insurance_claim', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.insurance_claim === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2515">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.insurance_claim === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2516">
                          {option.icon}
                        </div>
                        <div data-id="element-2517">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2518">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2519">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 6: Property Ownership */}
              {step === 6 && <motion.div key="step6" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2520">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2521">
                    Do you own your home?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2522">
                    {[{
                  value: 'Own',
                  desc: 'I own this property',
                  icon: <HomeIcon className="w-6 h-6" data-id="element-2523" />
                }, {
                  value: 'Rent',
                  desc: 'I rent this property',
                  icon: <UserIcon className="w-6 h-6" data-id="element-2524" />
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('property_ownership', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.property_ownership === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2525">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.property_ownership === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2526">
                          {option.icon}
                        </div>
                        <div data-id="element-2527">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2528">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2529">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* STEP 7: Address */}
              {step === 7 && <motion.div key="step7" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2530">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2531">
                    Where is your project located?
                  </h1>
                  <div className="space-y-5" data-id="element-2532">
                    <div data-id="element-2533">
                      <label htmlFor="address1" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2534">
                        Street Address
                      </label>
                      <input type="text" id="address1" name="address1" value={formData.address1} onChange={handleInputChange} onBlur={handleAddressBlur} className={`w-full h-14 px-4 bg-slate-50 border rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent ${addressError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`} placeholder="123 Main St" data-id="element-2535" />
                      {addressError && <p className="text-red-500 text-xs mt-1.5 font-medium" data-id="element-2536">
                          {addressError}
                        </p>}

                      <div className="mt-3 flex items-center gap-2" data-id="element-2537">
                        {zipLookupLoading ? <div className="flex items-center gap-2 text-slate-400 text-sm" data-id="element-2538">
                            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2539" />
                            <span data-id="element-2540">Looking up location...</span>
                          </div> : editingZip ? <div className="flex items-center gap-2 w-full" data-id="element-2541">
                            <MapPinIcon className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-2542" />
                            <input type="text" value={formData.postal_code} onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setFormData(prev => ({
                          ...prev,
                          postal_code: val,
                          city: '',
                          state: ''
                        }));
                        setZipLookupDone(false);
                      }} onBlur={() => {
                        if (formData.postal_code.length === 5) setEditingZip(false);
                      }} onKeyDown={e => {
                        if (e.key === 'Enter' && formData.postal_code.length === 5) setEditingZip(false);
                      }} placeholder="Enter ZIP" maxLength={5} autoFocus className="flex-1 h-10 px-3 bg-white border border-amber-300 rounded-lg text-sm font-semibold text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" data-id="element-2543" />
                          </div> : zipLookupDone && formData.city && formData.state ? <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-full hover:bg-amber-100 transition-colors cursor-pointer text-left" title="Click to change ZIP code" data-id="element-2544">
                            <MapPinIcon className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-2545" />
                            <span className="text-sm font-semibold text-amber-700" data-id="element-2546">
                              {formData.city}, {formData.state}{' '}
                              {formData.postal_code}
                            </span>
                            <span className="text-[10px] text-amber-400 ml-auto" data-id="element-2547">
                              edit
                            </span>
                          </button> : <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 w-full hover:bg-slate-100 transition-colors cursor-pointer text-left" title="Click to enter ZIP code" data-id="element-2548">
                            <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-2549" />
                            <span className="text-sm text-slate-500" data-id="element-2550">
                              {formData.postal_code ? `Zip: ${formData.postal_code}` : 'Enter your ZIP code'}
                            </span>
                          </button>}
                      </div>
                    </div>

                    <button onClick={() => {
                  if (!validateAddress(formData.address1)) {
                    setAddressError('Please enter a valid street address (e.g. 123 Main St)');
                    return;
                  }
                  handleNext();
                }} disabled={!formData.address1 || !formData.city || !formData.state || !formData.postal_code || !!addressError} className="w-full mt-6 h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold text-lg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2551">
                      Continue
                    </button>
                  </div>
                </motion.div>}

              {/* STEP 8: Contact Info */}
              {step === 8 && <motion.div key="step8" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2552">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2 text-center" data-id="element-2553">
                    Who should we send the quote to?
                  </h1>
                  <p className="text-slate-500 text-center mb-8" data-id="element-2554">
                    Last step! Your information is secure.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2555">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-2556">
                      <div data-id="element-2557">
                        <label htmlFor="first_name" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2558">
                          First Name
                        </label>
                        <input type="text" id="first_name" name="first_name" required value={formData.first_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="First Name" data-id="element-2559" />
                      </div>
                      <div data-id="element-2560">
                        <label htmlFor="last_name" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2561">
                          Last Name
                        </label>
                        <input type="text" id="last_name" name="last_name" required value={formData.last_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="Last Name" data-id="element-2562" />
                      </div>
                    </div>

                    <div data-id="element-2563">
                      <label htmlFor="email" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2564">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="email@example.com" data-id="element-2565" />
                    </div>

                    <div data-id="element-2566">
                      <label htmlFor="phone_primary" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2567">
                        Primary Phone
                      </label>
                      <input type="tel" id="phone_primary" name="phone_primary" required value={formData.phone_primary} onChange={handlePhoneChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="555-123-4567" maxLength={12} data-id="element-2568" />
                    </div>

                    <label className="flex items-start gap-3 mb-4 cursor-pointer" data-id="element-2569">
                      <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="mt-0.5 w-5 h-5 rounded border-slate-300 text-amber-500 focus:ring-amber-500 cursor-pointer flex-shrink-0" required data-id="element-2570" />
                      <span className="text-[11px] text-slate-500 leading-tight" data-id="element-2571">
                        By clicking the button, I provide my electronic
                        signature and express written consent to share my
                        contact information with FindServiceQuotes, including
                        their contractors and affiliates, and up to four home
                        service companies. I authorize them to email, call, or
                        text me for marketing purposes using autodialer
                        technology, AI generated or AI assisted voice, or
                        prerecorded voice messages on the phone number I
                        provided, even if listed on a DoNotCall list. Consent is
                        not a condition to purchase. I agree to the{' '}
                        <a href="#" className="underline text-amber-600" data-id="element-2572">
                          Terms of Use
                        </a>{' '}
                        and{' '}
                        <a href="#" className="underline text-amber-600" data-id="element-2573">
                          Privacy Policy
                        </a>
                        .
                      </span>
                    </label>
                    <button type="submit" disabled={isSubmitting || !consentChecked} className="w-full h-16 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xl rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-amber-500/30" data-id="element-2574">
                      {isSubmitting ? 'Processing...' : 'Get Your Free Quote'}
                    </button>
                  </form>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}
// ─── GENERIC QUOTE FORM (all other services) ───────────────────────────────
function GenericQuoteForm({
  initialService,
  initialZip,
  onClose
}: QuoteFormProps) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [zipLookupLoading, setZipLookupLoading] = useState(false);
  const [zipLookupDone, setZipLookupDone] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [editingZip, setEditingZip] = useState(false);
  const [formData, setFormData] = useState({
    serviceCategory: initialService || 'general',
    zipCode: initialZip || '',
    specificNeed: '',
    projectScope: '',
    timeline: '',
    homeownerStatus: '',
    address1: '',
    city: '',
    state: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  // Zip code lookup
  useEffect(() => {
    const zip = formData.zipCode;
    if (zip && zip.length === 5) {
      setZipLookupLoading(true);
      setZipLookupDone(false);
      fetch(`https://api.zippopotam.us/us/${zip}`).then(res => {
        if (!res.ok) throw new Error('Invalid zip');
        return res.json();
      }).then(data => {
        if (data.places && data.places.length > 0) {
          const place = data.places[0];
          setFormData(prev => ({
            ...prev,
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          }));
          setZipLookupDone(true);
        }
      }).catch(() => {
        setZipLookupDone(false);
      }).finally(() => {
        setZipLookupLoading(false);
      });
    }
  }, [formData.zipCode]);
  // Load TrustedForm script
  useEffect(() => {
    try {
      const tf = document.createElement('script');
      tf.type = 'text/javascript';
      tf.async = true;
      tf.src = 'https://api.trustedform.com/trustedform.js?provide_referrer=false&field=xxTrustedFormCertUrl&l=' + new Date().getTime() + Math.random();
      document.head.appendChild(tf);
    } catch (e) {
      console.warn('TrustedForm script could not be loaded:', e);
    }
  }, []);
  const totalSteps = 6;
  const progress = step / totalSteps * 100;
  const handleNext = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const handleOptionSelect = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setTimeout(() => {
      handleNext();
    }, 300);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'address1') setAddressError('');
  };
  const validateAddress = (address: string): boolean => {
    return /^\d+\s+\S{2,}/.test(address.trim());
  };
  const handleAddressBlur = () => {
    if (formData.address1 && !validateAddress(formData.address1)) {
      setAddressError('Please enter a valid street address (e.g. 123 Main St)');
    } else {
      setAddressError('');
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
    }, 1500);
  };
  const getServiceDisplayName = (key: string) => {
    const names: Record<string, string> = {
      'walk-in-showers': 'Walk-In Shower',
      'walk-in-tubs': 'Walk-In Tub',
      stairlifts: 'Stairlift',
      'window-replacement': 'Window Replacement',
      'gutter-guards': 'Gutter Guard',
      roofing: 'Roofing',
      siding: 'Siding',
      solar: 'Solar Panel',
      'bathroom-remodel': 'Bathroom Remodel',
      'kitchen-remodel': 'Kitchen Remodel',
      flooring: 'Flooring',
      hvac: 'HVAC',
      painting: 'Painting',
      plumbing: 'Plumbing',
      electrical: 'Electrical',
      fencing: 'Fencing',
      insulation: 'Insulation',
      doors: 'Door',
      'garage-doors': 'Garage Door',
      'home-security': 'Home Security',
      'pest-control': 'Pest Control',
      landscaping: 'Landscaping',
      cleaning: 'Cleaning',
      general: 'General Repair'
    };
    return names[key] || key;
  };
  const serviceName = getServiceDisplayName(formData.serviceCategory);
  const getServiceOptions = () => {
    const category = formData.serviceCategory.toLowerCase();
    const optionsMap: Record<string, {
      label: string;
      icon: React.ReactNode;
    }[]> = {
      'walk-in-showers': [{
        label: 'New Installation',
        icon: <ShowerHeadIcon className="w-6 h-6" data-id="element-2575" />
      }, {
        label: 'Replacement/Upgrade',
        icon: <ShowerHeadIcon className="w-6 h-6" data-id="element-2576" />
      }, {
        label: 'Conversion from Tub',
        icon: <BathIcon className="w-6 h-6" data-id="element-2577" />
      }, {
        label: 'Accessibility Retrofit',
        icon: <ArrowUpIcon className="w-6 h-6" data-id="element-2578" />
      }],
      'walk-in-tubs': [{
        label: 'New Installation',
        icon: <BathIcon className="w-6 h-6" data-id="element-2579" />
      }, {
        label: 'Replacement/Upgrade',
        icon: <BathIcon className="w-6 h-6" data-id="element-2580" />
      }, {
        label: 'Conversion from Shower',
        icon: <ShowerHeadIcon className="w-6 h-6" data-id="element-2581" />
      }, {
        label: 'Accessibility Features',
        icon: <ArrowUpIcon className="w-6 h-6" data-id="element-2582" />
      }],
      stairlifts: [{
        label: 'Straight Stairlift',
        icon: <ArrowUpIcon className="w-6 h-6" data-id="element-2583" />
      }, {
        label: 'Curved Stairlift',
        icon: <ArrowUpIcon className="w-6 h-6" data-id="element-2584" />
      }, {
        label: 'Outdoor Stairlift',
        icon: <ArrowUpIcon className="w-6 h-6" data-id="element-2585" />
      }, {
        label: 'Repair/Service',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2586" />
      }],
      'gutter-guards': [{
        label: 'New Gutter Guards',
        icon: <LayersIcon className="w-6 h-6" data-id="element-2587" />
      }, {
        label: 'Gutter Replacement',
        icon: <LayersIcon className="w-6 h-6" data-id="element-2588" />
      }, {
        label: 'Gutter Repair',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2589" />
      }, {
        label: 'Cleaning & Maintenance',
        icon: <SparklesIcon className="w-6 h-6" data-id="element-2590" />
      }],
      roofing: [{
        label: 'Roof Repair',
        icon: <HomeIcon className="w-6 h-6" data-id="element-2591" />
      }, {
        label: 'Full Replacement',
        icon: <HomeIcon className="w-6 h-6" data-id="element-2592" />
      }, {
        label: 'Inspection',
        icon: <CheckCircle2Icon className="w-6 h-6" data-id="element-2593" />
      }, {
        label: 'Storm Damage',
        icon: <HomeIcon className="w-6 h-6" data-id="element-2594" />
      }],
      siding: [{
        label: 'Full Replacement',
        icon: <HomeIcon className="w-6 h-6" data-id="element-2595" />
      }, {
        label: 'Partial Repair',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2596" />
      }, {
        label: 'New Installation',
        icon: <HammerIcon className="w-6 h-6" data-id="element-2597" />
      }, {
        label: 'Inspection/Estimate',
        icon: <SearchIcon className="w-6 h-6" data-id="element-2598" />
      }],
      solar: [{
        label: 'Rooftop Solar Panels',
        icon: <SunIcon className="w-6 h-6" data-id="element-2599" />
      }, {
        label: 'Solar Battery Storage',
        icon: <ZapIcon className="w-6 h-6" data-id="element-2600" />
      }, {
        label: 'Solar Roof Tiles',
        icon: <SunIcon className="w-6 h-6" data-id="element-2601" />
      }, {
        label: 'Consultation/Quote',
        icon: <SearchIcon className="w-6 h-6" data-id="element-2602" />
      }],
      'bathroom-remodel': [{
        label: 'Full Remodel',
        icon: <ShowerHeadIcon className="w-6 h-6" data-id="element-2603" />
      }, {
        label: 'Shower/Tub Only',
        icon: <BathIcon className="w-6 h-6" data-id="element-2604" />
      }, {
        label: 'Vanity & Fixtures',
        icon: <SparklesIcon className="w-6 h-6" data-id="element-2605" />
      }, {
        label: 'Tile & Flooring',
        icon: <FootprintsIcon className="w-6 h-6" data-id="element-2606" />
      }],
      'kitchen-remodel': [{
        label: 'Full Remodel',
        icon: <CookingPotIcon className="w-6 h-6" data-id="element-2607" />
      }, {
        label: 'Cabinets & Countertops',
        icon: <CookingPotIcon className="w-6 h-6" data-id="element-2608" />
      }, {
        label: 'Appliance Installation',
        icon: <HammerIcon className="w-6 h-6" data-id="element-2609" />
      }, {
        label: 'Backsplash & Tile',
        icon: <FootprintsIcon className="w-6 h-6" data-id="element-2610" />
      }],
      flooring: [{
        label: 'Hardwood',
        icon: <FootprintsIcon className="w-6 h-6" data-id="element-2611" />
      }, {
        label: 'Tile',
        icon: <FootprintsIcon className="w-6 h-6" data-id="element-2612" />
      }, {
        label: 'Carpet',
        icon: <FootprintsIcon className="w-6 h-6" data-id="element-2613" />
      }, {
        label: 'Vinyl/Laminate',
        icon: <FootprintsIcon className="w-6 h-6" data-id="element-2614" />
      }],
      plumbing: [{
        label: 'Repair/Fix Issue',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2615" />
      }, {
        label: 'New Installation',
        icon: <HammerIcon className="w-6 h-6" data-id="element-2616" />
      }, {
        label: 'Drain Cleaning',
        icon: <SparklesIcon className="w-6 h-6" data-id="element-2617" />
      }, {
        label: 'Water Heater',
        icon: <ThermometerIcon className="w-6 h-6" data-id="element-2618" />
      }],
      electrical: [{
        label: 'Wiring/Rewiring',
        icon: <ZapIcon className="w-6 h-6" data-id="element-2619" />
      }, {
        label: 'Panel Upgrade',
        icon: <ZapIcon className="w-6 h-6" data-id="element-2620" />
      }, {
        label: 'Lighting Installation',
        icon: <ZapIcon className="w-6 h-6" data-id="element-2621" />
      }, {
        label: 'Outlet/Switch Repair',
        icon: <ZapIcon className="w-6 h-6" data-id="element-2622" />
      }],
      hvac: [{
        label: 'AC Repair',
        icon: <ThermometerIcon className="w-6 h-6" data-id="element-2623" />
      }, {
        label: 'Heating Repair',
        icon: <ThermometerIcon className="w-6 h-6" data-id="element-2624" />
      }, {
        label: 'New System Install',
        icon: <HammerIcon className="w-6 h-6" data-id="element-2625" />
      }, {
        label: 'Routine Maintenance',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2626" />
      }],
      painting: [{
        label: 'Interior Painting',
        icon: <PaintbrushIcon className="w-6 h-6" data-id="element-2627" />
      }, {
        label: 'Exterior Painting',
        icon: <PaintbrushIcon className="w-6 h-6" data-id="element-2628" />
      }, {
        label: 'Cabinet Painting',
        icon: <PaintbrushIcon className="w-6 h-6" data-id="element-2629" />
      }, {
        label: 'Both Int/Ext',
        icon: <PaintbrushIcon className="w-6 h-6" data-id="element-2630" />
      }],
      fencing: [{
        label: 'New Fence Install',
        icon: <FenceIcon className="w-6 h-6" data-id="element-2631" />
      }, {
        label: 'Fence Repair',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2632" />
      }, {
        label: 'Gate Installation',
        icon: <DoorOpenIcon className="w-6 h-6" data-id="element-2633" />
      }, {
        label: 'Fence Replacement',
        icon: <FenceIcon className="w-6 h-6" data-id="element-2634" />
      }],
      insulation: [{
        label: 'Attic Insulation',
        icon: <SnowflakeIcon className="w-6 h-6" data-id="element-2635" />
      }, {
        label: 'Wall Insulation',
        icon: <SnowflakeIcon className="w-6 h-6" data-id="element-2636" />
      }, {
        label: 'Spray Foam',
        icon: <SnowflakeIcon className="w-6 h-6" data-id="element-2637" />
      }, {
        label: 'Removal & Replace',
        icon: <HammerIcon className="w-6 h-6" data-id="element-2638" />
      }],
      doors: [{
        label: 'Front Door',
        icon: <DoorOpenIcon className="w-6 h-6" data-id="element-2639" />
      }, {
        label: 'Patio/Sliding Door',
        icon: <DoorOpenIcon className="w-6 h-6" data-id="element-2640" />
      }, {
        label: 'Interior Doors',
        icon: <DoorOpenIcon className="w-6 h-6" data-id="element-2641" />
      }, {
        label: 'Door Repair',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2642" />
      }],
      'garage-doors': [{
        label: 'New Installation',
        icon: <WarehouseIcon className="w-6 h-6" data-id="element-2643" />
      }, {
        label: 'Replacement',
        icon: <WarehouseIcon className="w-6 h-6" data-id="element-2644" />
      }, {
        label: 'Opener Install/Repair',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2645" />
      }, {
        label: 'Repair/Maintenance',
        icon: <WrenchIcon className="w-6 h-6" data-id="element-2646" />
      }],
      'home-security': [{
        label: 'Full System Install',
        icon: <ShieldIcon className="w-6 h-6" data-id="element-2647" />
      }, {
        label: 'Camera System',
        icon: <ShieldIcon className="w-6 h-6" data-id="element-2648" />
      }, {
        label: 'Smart Lock Install',
        icon: <DoorOpenIcon className="w-6 h-6" data-id="element-2649" />
      }, {
        label: 'Alarm System',
        icon: <ShieldIcon className="w-6 h-6" data-id="element-2650" />
      }],
      'pest-control': [{
        label: 'General Pest Control',
        icon: <BugIcon className="w-6 h-6" data-id="element-2651" />
      }, {
        label: 'Termite Treatment',
        icon: <BugIcon className="w-6 h-6" data-id="element-2652" />
      }, {
        label: 'Rodent Control',
        icon: <BugIcon className="w-6 h-6" data-id="element-2653" />
      }, {
        label: 'Wildlife Removal',
        icon: <BugIcon className="w-6 h-6" data-id="element-2654" />
      }],
      cleaning: [{
        label: 'Regular Cleaning',
        icon: <SparklesIcon className="w-6 h-6" data-id="element-2655" />
      }, {
        label: 'Deep Cleaning',
        icon: <SparklesIcon className="w-6 h-6" data-id="element-2656" />
      }, {
        label: 'Move-in/Move-out',
        icon: <HomeIcon className="w-6 h-6" data-id="element-2657" />
      }, {
        label: 'Post-Construction',
        icon: <HammerIcon className="w-6 h-6" data-id="element-2658" />
      }],
      landscaping: [{
        label: 'Lawn Care/Mowing',
        icon: <TreesIcon className="w-6 h-6" data-id="element-2659" />
      }, {
        label: 'Landscape Design',
        icon: <TreesIcon className="w-6 h-6" data-id="element-2660" />
      }, {
        label: 'Tree Service',
        icon: <TreesIcon className="w-6 h-6" data-id="element-2661" />
      }, {
        label: 'Hardscaping',
        icon: <HammerIcon className="w-6 h-6" data-id="element-2662" />
      }]
    };
    return optionsMap[category] || [{
      label: 'Repair/Maintenance',
      icon: <WrenchIcon className="w-6 h-6" data-id="element-2663" />
    }, {
      label: 'New Installation',
      icon: <HammerIcon className="w-6 h-6" data-id="element-2664" />
    }, {
      label: 'Replacement',
      icon: <CheckCircle2Icon className="w-6 h-6" data-id="element-2665" />
    }, {
      label: 'Other',
      icon: <CheckCircle2Icon className="w-6 h-6" data-id="element-2666" />
    }];
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  if (isSuccess) {
    return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-2667">
        <header className="bg-white border-b border-slate-200 py-4 px-6 flex justify-between items-center" data-id="element-2668">
          <div className="text-xl font-extrabold text-slate-900 tracking-tight" data-id="element-2669">
            FindService<span className="text-amber-500" data-id="element-2670">Quotes</span>
          </div>
          <button onClick={onClose} className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors" data-id="element-2671">
            Return to Home
          </button>
        </header>
        <div className="flex-1 flex items-center justify-center p-6" data-id="element-2672">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center border border-slate-100" data-id="element-2673">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2674">
              <CheckCircle2Icon className="w-10 h-10 text-green-600" data-id="element-2675" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2676">
              Request Received!
            </h2>
            <p className="text-slate-600 mb-8" data-id="element-2677">
              Thanks {formData.firstName}! We're matching you with top-rated{' '}
              {serviceName} pros in {formData.zipCode}. They will contact you
              shortly.
            </p>
            <button onClick={onClose} className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors" data-id="element-2678">
              Done
            </button>
          </motion.div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-2679">
      <header className="bg-white border-b border-slate-200 py-4 px-4 md:px-8 flex justify-between items-center sticky top-0 z-10" data-id="element-2680">
        <div className="text-xl font-extrabold text-slate-900 tracking-tight" data-id="element-2681">
          FindService<span className="text-amber-500" data-id="element-2682">Quotes</span>
        </div>
        <button onClick={onClose} className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1" data-id="element-2683">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-2684" />
          <span className="hidden sm:inline" data-id="element-2685">Exit</span>
        </button>
      </header>

      <div className="w-full bg-slate-200 h-2" data-id="element-2686">
        <motion.div className="bg-amber-500 h-full" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-2687" />
      </div>

      <main className="flex-1 flex flex-col items-center pt-8 pb-20 px-4" data-id="element-2688">
        <div className="w-full max-w-2xl" data-id="element-2689">
          <div className="mb-6 h-8" data-id="element-2690">
            {step > 1 && <button onClick={handleBack} className="text-slate-500 hover:text-slate-900 font-medium flex items-center gap-2 transition-colors" data-id="element-2691">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2692" /> Back
              </button>}
          </div>

          <div className="relative" data-id="element-2693">
            <AnimatePresence mode="wait" custom={direction} data-id="element-2694">
              {step === 1 && <motion.div key="step1" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="w-full" data-id="element-2695">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2 text-center" data-id="element-2696">
                    What type of {serviceName} service do you need?
                  </h1>
                  <p className="text-slate-500 text-center mb-8 flex items-center justify-center gap-2" data-id="element-2697">
                    <MapPinIcon className="w-4 h-4" data-id="element-2698" /> Zip Code:{' '}
                    {formData.zipCode || 'Not provided'}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2699">
                    {getServiceOptions().map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('specificNeed', option.label)} className={`p-6 rounded-xl border-2 text-left flex flex-col items-center justify-center gap-4 transition-all duration-200 ${formData.specificNeed === option.label ? 'border-amber-500 bg-amber-50 shadow-md ring-1 ring-amber-500' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2700">
                        <div className={`p-3 rounded-full ${formData.specificNeed === option.label ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-600'}`} data-id="element-2701">
                          {option.icon}
                        </div>
                        <span className="font-semibold text-slate-900 text-lg" data-id="element-2702">
                          {option.label}
                        </span>
                      </button>)}
                  </div>
                </motion.div>}

              {step === 2 && <motion.div key="step2" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="w-full" data-id="element-2703">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2704">
                    What is the scope of your {serviceName} project?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2705">
                    {[{
                  value: 'Complete Replacement',
                  desc: 'Entire system to be replaced',
                  icon: <ZapIcon className="w-6 h-6" data-id="element-2706" />
                }, {
                  value: 'Partial Replacement',
                  desc: 'Some components to be replaced',
                  icon: <WrenchIcon className="w-6 h-6" data-id="element-2707" />
                }, {
                  value: 'Repair/Upgrade',
                  desc: 'Fix existing issues and improve performance',
                  icon: <CheckCircle2Icon className="w-6 h-6" data-id="element-2708" />
                }, {
                  value: 'New Installation',
                  desc: 'Entire new system to be installed',
                  icon: <HammerIcon className="w-6 h-6" data-id="element-2709" />
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('projectScope', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.projectScope === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2710">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.projectScope === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2711">
                          {option.icon}
                        </div>
                        <div data-id="element-2712">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2713">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2714">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {step === 3 && <motion.div key="step3" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="w-full" data-id="element-2715">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2716">
                    How long do you need this completed?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2717">
                    {[{
                  value: 'Immediate',
                  desc: 'Need it done as soon as possible',
                  icon: <ZapIcon className="w-6 h-6" data-id="element-2718" />
                }, {
                  value: 'Within 1 Week',
                  desc: 'Ready to start in the next week',
                  icon: <ClockIcon className="w-6 h-6" data-id="element-2719" />
                }, {
                  value: '1-3 Weeks',
                  desc: 'Planning ahead for a few weeks',
                  icon: <ClockIcon className="w-6 h-6" data-id="element-2720" />
                }, {
                  value: '3-6 Weeks',
                  desc: 'Flexible timeline for several weeks',
                  icon: <ClockIcon className="w-6 h-6" data-id="element-2721" />
                }, {
                  value: 'Just Researching',
                  desc: 'Gathering information and quotes',
                  icon: <SearchIcon className="w-6 h-6" data-id="element-2722" />
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('timeline', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.timeline === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2723">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.timeline === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2724">
                          {option.icon}
                        </div>
                        <div data-id="element-2725">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2726">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2727">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {step === 4 && <motion.div key="step4" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="w-full" data-id="element-2728">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2729">
                    Do you own your home?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2730">
                    {[{
                  value: 'Yes',
                  desc: 'I own this property'
                }, {
                  value: 'No - But I am Allowed to make changes',
                  desc: 'I rent but have permission from my landlord'
                }, {
                  value: 'No',
                  desc: 'I rent and do not have permission'
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('homeownerStatus', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.homeownerStatus === option.value ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'}`} data-id="element-2731">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.homeownerStatus === option.value ? 'bg-amber-100 text-amber-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2732">
                          <UserIcon className="w-6 h-6" data-id="element-2733" />
                        </div>
                        <div data-id="element-2734">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-2735">
                            {option.value}
                          </div>
                          <div className="text-slate-500 text-sm" data-id="element-2736">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {step === 5 && <motion.div key="step5" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="w-full" data-id="element-2737">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-2738">
                    Where is your project located?
                  </h1>
                  <div className="space-y-5" data-id="element-2739">
                    <div data-id="element-2740">
                      <label htmlFor="address1" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2741">
                        Street Address
                      </label>
                      <input type="text" id="address1" name="address1" value={formData.address1} onChange={handleInputChange} onBlur={handleAddressBlur} className={`w-full h-14 px-4 bg-slate-50 border rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent ${addressError ? 'border-red-400 bg-red-50' : 'border-slate-300'}`} placeholder="123 Main St" data-id="element-2742" />
                      {addressError && <p className="text-red-500 text-xs mt-1.5 font-medium" data-id="element-2743">
                          {addressError}
                        </p>}

                      <div className="mt-3 flex items-center gap-2" data-id="element-2744">
                        {zipLookupLoading ? <div className="flex items-center gap-2 text-slate-400 text-sm" data-id="element-2745">
                            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2746" />
                            <span data-id="element-2747">Looking up location...</span>
                          </div> : editingZip ? <div className="flex items-center gap-2 w-full" data-id="element-2748">
                            <MapPinIcon className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-2749" />
                            <input type="text" value={formData.postal_code} onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setFormData(prev => ({
                          ...prev,
                          postal_code: val,
                          city: '',
                          state: ''
                        }));
                        setZipLookupDone(false);
                      }} onBlur={() => {
                        if (formData.postal_code.length === 5) setEditingZip(false);
                      }} onKeyDown={e => {
                        if (e.key === 'Enter' && formData.postal_code.length === 5) setEditingZip(false);
                      }} placeholder="Enter ZIP" maxLength={5} autoFocus className="flex-1 h-10 px-3 bg-white border border-amber-300 rounded-lg text-sm font-semibold text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" data-id="element-2750" />
                          </div> : zipLookupDone && formData.city && formData.state ? <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-full hover:bg-amber-100 transition-colors cursor-pointer text-left" title="Click to change ZIP code" data-id="element-2751">
                            <MapPinIcon className="w-4 h-4 text-amber-600 flex-shrink-0" data-id="element-2752" />
                            <span className="text-sm font-semibold text-amber-700" data-id="element-2753">
                              {formData.city}, {formData.state}{' '}
                              {formData.postal_code}
                            </span>
                            <span className="text-[10px] text-amber-400 ml-auto" data-id="element-2754">
                              edit
                            </span>
                          </button> : <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 w-full hover:bg-slate-100 transition-colors cursor-pointer text-left" title="Click to enter ZIP code" data-id="element-2755">
                            <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-2756" />
                            <span className="text-sm text-slate-500" data-id="element-2757">
                              {formData.postal_code ? `Zip: ${formData.postal_code}` : 'Enter your ZIP code'}
                            </span>
                          </button>}
                      </div>
                    </div>

                    <button onClick={() => {
                  if (!validateAddress(formData.address1)) {
                    setAddressError('Please enter a valid street address (e.g. 123 Main St)');
                    return;
                  }
                  handleNext();
                }} disabled={!formData.address1 || !formData.city || !formData.state || !formData.postal_code || !!addressError} className="w-full mt-6 h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold text-lg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2758">
                      Continue
                    </button>
                  </div>
                </motion.div>}

              {step === 6 && <motion.div key="step6" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="w-full" data-id="element-2759">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2 text-center" data-id="element-2760">
                    Who should we send the quote to?
                  </h1>
                  <p className="text-slate-500 text-center mb-8" data-id="element-2761">
                    Last step! Your information is secure.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2762">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-2763">
                      <div data-id="element-2764">
                        <label htmlFor="firstName" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2765">
                          First Name
                        </label>
                        <input type="text" id="firstName" name="firstName" required value={formData.firstName} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="First Name" data-id="element-2766" />
                      </div>
                      <div data-id="element-2767">
                        <label htmlFor="lastName" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2768">
                          Last Name
                        </label>
                        <input type="text" id="lastName" name="lastName" required value={formData.lastName} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="Last Name" data-id="element-2769" />
                      </div>
                    </div>

                    <div data-id="element-2770">
                      <label htmlFor="email" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2771">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="email@example.com" data-id="element-2772" />
                    </div>

                    <div data-id="element-2773">
                      <label htmlFor="phone" className="block text-sm font-bold text-slate-700 mb-1" data-id="element-2774">
                        Primary Phone
                      </label>
                      <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleInputChange} className="w-full h-14 px-4 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="555-123-4567" maxLength={12} data-id="element-2775" />
                    </div>

                    <div className="pt-4" data-id="element-2776">
                      <label className="flex items-start gap-3 mb-4 cursor-pointer" data-id="element-2777">
                        <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="mt-0.5 w-5 h-5 rounded border-slate-300 text-amber-500 focus:ring-amber-500 cursor-pointer flex-shrink-0" required data-id="element-2778" />
                        <span className="text-[11px] text-slate-500 leading-tight" data-id="element-2779">
                          By clicking the button, I provide my electronic
                          signature and express written consent to share my
                          contact information with FindServiceQuotes, including
                          their contractors and affiliates, and up to four home
                          service companies. I authorize them to email, call, or
                          text me for marketing purposes using autodialer
                          technology, AI generated or AI assisted voice, or
                          prerecorded voice messages on the phone number I
                          provided, even if listed on a DoNotCall list. Consent
                          is not a condition to purchase. I agree to the{' '}
                          <a href="#" className="underline text-amber-600" data-id="element-2780">
                            Terms of Use
                          </a>{' '}
                          and{' '}
                          <a href="#" className="underline text-amber-600" data-id="element-2781">
                            Privacy Policy
                          </a>
                          .
                        </span>
                      </label>
                      <button type="submit" disabled={isSubmitting || !consentChecked} className="w-full h-16 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xl rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-amber-500/30" data-id="element-2782">
                        {isSubmitting ? 'Processing...' : 'Get Your Free Quote'}
                      </button>
                    </div>
                  </form>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}